import {
  finiteOrNull,
  measToNumber,
  toMeas,
  WORKING_UNIT_PRESSURE,
  WORKING_UNIT_THICKNESS,
} from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import {
  GeckoComponentModel,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

/**
 * Snapshot fields read off the ticket's typed asset-model component. Only
 * `GeckoPipeSection` carries these today; structurally narrowed the same way
 * `computeMinThicknessValidation` does, so other component types (no matching
 * fields) fall through to `undefined` -- every prefill value below is then
 * `null`, and the MAWP card falls back to its hardcoded UI defaults.
 */
interface MawpSnapshotComponentFields {
  designPressure?: GeckoMeasurement
  designTemp?: GeckoMeasurement
  allowableStress?: GeckoMeasurement
  jointEfficiency?: number
  pressureCoefficient?: number
  weldStrengthReduction?: number
  tsl?: GeckoMeasurement
}

export interface MawpSnapshotPrefill {
  /** P -- design pressure. */
  designPressure: GeckoMeasurement | null
  /** T -- design temperature, normalized to Celsius. */
  designTemp: GeckoMeasurement | null
  /**
   * S -- the MAWP card's allowable-stress field. Deliberately reads
   * `allowableStress`, NOT `yieldStrength` (which
   * `computeMinThicknessValidation` uses for the Minimum Thickness step's S)
   * -- the MAWP card's "S" is the API 579 allowable stress term, and the
   * asset model tracks it as a separate field from yield strength.
   */
  allowableStress: GeckoMeasurement | null
  /** E -- joint efficiency / quality factor. */
  qualityFactor: GeckoMeasurement | null
  /** Y -- pressure coefficient. */
  pressureCoefficient: GeckoMeasurement | null
  /** W -- weld joint strength reduction factor. */
  weldStrengthReduction: GeckoMeasurement | null
  /** t_sl -- thickness for supplemental (longitudinal) loads. */
  tsl: GeckoMeasurement | null
}

/**
 * Base values to prefill the Part 4/5 MAWP calc card's operating params
 * (P, T, S, E, Y, t_sl) from the ticket's asset-model snapshot. Each field is
 * `null` when the snapshot doesn't carry it (missing component, non-piping
 * component type, or a field the user never populated on the asset model) --
 * the caller (`FfsSharedValues`) falls back to a hardcoded UI default in that
 * case. This is a plain function, not a hook -- callers wrap it in `useMemo`
 * keyed on `componentModel` for React-side memoization.
 */
export function computeMawpSnapshotPrefill(
  componentModel: GeckoComponentModel | undefined
): MawpSnapshotPrefill {
  const cm = componentModel as MawpSnapshotComponentFields | undefined

  return {
    designPressure: toMeas(
      measToNumber(cm?.designPressure, WORKING_UNIT_PRESSURE),
      WORKING_UNIT_PRESSURE
    ),
    designTemp: toMeas(
      measToNumber(cm?.designTemp, GeckoMeasurementUnit.Celsius),
      GeckoMeasurementUnit.Celsius
    ),
    allowableStress: toMeas(
      measToNumber(cm?.allowableStress, WORKING_UNIT_PRESSURE),
      WORKING_UNIT_PRESSURE
    ),
    qualityFactor: toMeas(
      finiteOrNull(cm?.jointEfficiency),
      GeckoMeasurementUnit.Ratio
    ),
    pressureCoefficient: toMeas(
      finiteOrNull(cm?.pressureCoefficient),
      GeckoMeasurementUnit.Ratio
    ),
    weldStrengthReduction: toMeas(
      finiteOrNull(cm?.weldStrengthReduction),
      GeckoMeasurementUnit.Ratio
    ),
    tsl: toMeas(
      measToNumber(cm?.tsl, WORKING_UNIT_THICKNESS),
      WORKING_UNIT_THICKNESS
    ),
  }
}
